import { Fragment, type MouseEvent, type ReactNode } from "react";
import { Link } from "@/shared/components/RouteDialog";

/**
 * Крошечный безопасный markdown для ответов помощника: абзацы, переводы
 * строк, **жирный**, [текст](url), списки "- " и "1. ". Всё прочее идёт
 * текстом, а React экранирует его сам: HTML из ответа модели в DOM не
 * попадает. Библиотеку не берём, поддержки больше не нужно.
 */
export interface AssistantMarkdownOptions {
  /** Хост витрины: абсолютные ссылки на него ведут внутрь. */
  currentHost?: string;
  /** Клик по внутренней ссылке (на телефоне панель закрывается). */
  onInternalLinkClick?: () => void;
}

type ResolvedHref =
  | { kind: "internal"; path: string }
  | { kind: "external"; href: string };

// Путь от корня витрины. "//host" и "/\\host" браузер читает как адрес
// другого сайта, их внутренними не считаем.
const INTERNAL_PATH_RE = /^\/(?![/\\])/;

function resolveHref(
  url: string,
  currentHost: string | undefined,
): ResolvedHref | null {
  if (INTERNAL_PATH_RE.test(url)) {
    return { kind: "internal", path: url };
  }
  if (!/^https?:\/\//i.test(url)) return null;
  try {
    const parsed = new URL(url);
    if (currentHost && parsed.host === currentHost) {
      const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      return INTERNAL_PATH_RE.test(path) ? { kind: "internal", path } : null;
    }
    return { kind: "external", href: url };
  } catch {
    return null;
  }
}

// **жирный** | [текст](url), в url допускается одна пара скобок.
const INLINE_RE =
  /\*\*([\s\S]+?)\*\*|\[([^\]\n]+)\]\(([^()\s]+(?:\([^()\s]*\)[^()\s]*)?)\)/g;

function renderInline(
  text: string,
  options: AssistantMarkdownOptions,
  keyPrefix: string,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let index = 0;
  for (const match of text.matchAll(INLINE_RE)) {
    const start = match.index ?? 0;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const key = `${keyPrefix}-${index++}`;
    const [, bold, label, url] = match;
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{renderInline(bold, options, key)}</strong>);
    } else if (label !== undefined && url !== undefined) {
      nodes.push(renderLink(label, url, options, key));
    }
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function renderLink(
  label: string,
  url: string,
  options: AssistantMarkdownOptions,
  key: string,
): ReactNode {
  const resolved = resolveHref(url, options.currentHost);
  // Неразрешённая схема (javascript:, mailto: и т.п.): только текст.
  if (!resolved) return <Fragment key={key}>{label}</Fragment>;
  if (resolved.kind === "external") {
    return (
      <a
        key={key}
        href={resolved.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  // Кастомный Link открывает диалоговые роуты (/video/...) мимо роутера,
  // остальные пути отдаёт next/link.
  const onClick = options.onInternalLinkClick
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        if (event.button === 0 && !event.metaKey && !event.ctrlKey) {
          options.onInternalLinkClick?.();
        }
      }
    : undefined;
  return (
    <Link key={key} href={resolved.path} onClick={onClick}>
      {label}
    </Link>
  );
}

const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;

type BlockPart =
  | { type: "p"; lines: string[] }
  | { type: "ul" | "ol"; items: string[] };

function splitBlock(block: string): BlockPart[] {
  const parts: BlockPart[] = [];
  for (const line of block.split("\n")) {
    const bullet = BULLET_RE.exec(line)?.[1];
    const ordered =
      bullet === undefined ? ORDERED_RE.exec(line)?.[1] : undefined;
    const item = bullet ?? ordered;
    const listType = bullet !== undefined ? "ul" : "ol";
    const previous = parts[parts.length - 1];
    if (item !== undefined) {
      if (previous && previous.type === listType) previous.items.push(item);
      else parts.push({ type: listType, items: [item] });
    } else if (line.trim()) {
      if (previous?.type === "p") previous.lines.push(line);
      else parts.push({ type: "p", lines: [line] });
    }
  }
  return parts;
}

export function renderAssistantMarkdown(
  text: string,
  options: AssistantMarkdownOptions = {},
): ReactNode[] {
  const resolvedOptions: AssistantMarkdownOptions = {
    ...options,
    currentHost:
      options.currentHost ??
      (typeof window !== "undefined" ? window.location.host : undefined),
  };
  const nodes: ReactNode[] = [];
  text
    .split(/\n\s*\n/)
    .filter((block) => block.trim())
    .forEach((block, blockIndex) => {
      splitBlock(block).forEach((part, partIndex) => {
        const key = `b${blockIndex}-${partIndex}`;
        if (part.type === "p") {
          nodes.push(
            <p key={key}>
              {part.lines.map((line, lineIndex) => (
                <Fragment key={lineIndex}>
                  {lineIndex > 0 && <br />}
                  {renderInline(line, resolvedOptions, `${key}-${lineIndex}`)}
                </Fragment>
              ))}
            </p>,
          );
          return;
        }
        const Tag = part.type;
        nodes.push(
          <Tag key={key}>
            {part.items.map((item, itemIndex) => (
              <li key={itemIndex}>
                {renderInline(item, resolvedOptions, `${key}-${itemIndex}`)}
              </li>
            ))}
          </Tag>,
        );
      });
    });
  return nodes;
}
